import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Box, Chip, ThemeProvider, Typography} from '@mui/material';
import axios from 'axios';
import {toast} from 'react-toastify';
import NavBar from '../../../pieces/navbar/navbar';
import myTheme from '../../../styles/myStyles';
import MyGrid from '../../../pieces/grids/gridWrapper';
import ColumnDefinition from '../../../pieces/grids/columnGenerator';
import ConfirmModal from '../../../pieces/modals/confirmPopup';
import FormSubmitButtons from '../../../pieces/formSubmitButtons';
import ApiUtils from '../../../utils/apiUtils';
import base_url from '../../../baseUrls';
import {COLORS} from '../../../styles/styleConstants';

const REFRESH_INTERVAL_SECONDS = 30;

const FIELDS = {
  JOB_NUMBER: {api_name: 'job_number', label: 'Job #'},
  AGENT_NAME: {api_name: 'agent_name', label: 'Agent'},
  PHONE: {api_name: 'phone', label: 'Phone'},
  STATUS: {api_name: 'status', label: 'Dialer Status'},
  STATE: {api_name: 'state', label: 'State'},
  AGE: {api_name: 'age_minutes', label: 'Open For (min)'},
  STARTED_AT: {api_name: 'started_at_display', label: 'Started'},
  CALL_SEQ: {api_name: 'call_seq', label: 'Call Seq'},
  ID: {api_name: 'id', label: 'Id'},
};

/**
 * A row's state, in the order that matters when triaging.
 *
 * `Unacknowledged` is its own state rather than a flavour of stale: it means TaskRunner dispatched
 * the call and the dialer never came back at all, which is a different failure from a call that
 * started and never reported finishing. Showing them as one hides which end is broken.
 */
const rowState = (row) => {
  if (!row?.acknowledged) return 'unacknowledged';
  return row?.is_stale ? 'stale' : 'live';
};

const STATE_LABEL = {
  live: 'Live',
  stale: 'Stale',
  unacknowledged: 'Never acknowledged',
};

const STATE_COLOUR = {
  live: COLORS.globalGreen ?? '#2e7d32',
  stale: COLORS.globalOrange ?? '#ed6c02',
  unacknowledged: COLORS.globalRed,
};

/**
 * Colour comes from `state_key` while the cell's own value is the human label, so the set filter
 * lists "Stale" rather than "stale" without a second lookup table to keep in step.
 */
const StateRenderer = (params) => {
  const key = params?.data?.state_key ?? 'live';
  return (
    <Chip
      label={STATE_LABEL[key] ?? key}
      size={'small'}
      sx={{backgroundColor: STATE_COLOUR[key] ?? COLORS.fontPale, color: '#fff'}}
    />
  );
};

/**
 * Every AI call still open, with the ability to close one out by hand.
 *
 * This exists because nothing reliably reports a call as finished yet. Until the dialer sends a
 * call-ended event, a call that drops leaves its row open forever — and an open row holds a slot
 * in the concurrency count that decides how many calls get dispatched. Closing it here is what
 * gives the slot back.
 */
export const AiActiveCalls = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [staleAfterMinutes, setStaleAfterMinutes] = useState(30);
  const [totalOpen, setTotalOpen] = useState(0);
  /** The call waiting on confirmation before it is closed. */
  const [pendingEnd, setPendingEnd] = useState(null);
  const [savingEnd, setSavingEnd] = useState(false);
  const [components] = useState({stateRenderer: StateRenderer});
  const gridApiRef = useRef(null);
  const isMountedRef = useRef(false);
  const isFetchingRef = useRef(false);

  const fetchRows = useCallback(async ({showLoader = false} = {}) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    if (showLoader && isMountedRef.current) setLoading(true);
    try {
      const authToken = await ApiUtils.getAccessToken();
      const response = await axios.get(`${base_url.api}ai/open-calls`, {
        headers: {Authorization: authToken},
      });
      const payload = response?.data?.data;
      const list = payload?.calls;
      if (isMountedRef.current) {
        setRows(Array.isArray(list) ? list : []);
        if (payload?.staleAfterMinutes) setStaleAfterMinutes(payload.staleAfterMinutes);
        setTotalOpen(Number(payload?.totalOpen ?? (Array.isArray(list) ? list.length : 0)));
        setError('');
      }
    } catch (e) {
      if (isMountedRef.current) setError('Unable to load active AI calls.');
    } finally {
      if (isMountedRef.current) setLoading(false);
      isFetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    fetchRows({showLoader: true});
    const id = setInterval(() => fetchRows(), REFRESH_INTERVAL_SECONDS * 1000);
    return () => {
      isMountedRef.current = false;
      clearInterval(id);
    };
  }, [fetchRows]);

  const gridRows = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        state_key: rowState(row),
        state: STATE_LABEL[rowState(row)],
        started_at_display: row?.started_at ? new Date(row.started_at).toLocaleString() : '',
      })),
    [rows],
  );

  const closeEndConfirm = () => {
    if (savingEnd) return;
    setPendingEnd(null);
  };

  const submitEnd = async () => {
    const target = pendingEnd;
    if (!target?.id) return;

    setSavingEnd(true);
    try {
      const authToken = await ApiUtils.getAccessToken();
      await axios.post(
        `${base_url.api}ai/end-call`,
        {aiCallId: target.id},
        {headers: {Authorization: authToken}},
      );
      toast(`Call ${target.label} marked as ended.`, {type: 'success'});
      setPendingEnd(null);
      fetchRows();
    } catch (err) {
      const message = err?.response?.data?.message ?? 'Could not mark this call as ended.';
      toast(message, {type: 'error'});
    } finally {
      setSavingEnd(false);
    }
  };

  const requestEnd = (rowData) => {
    setPendingEnd({
      id: rowData?.id,
      label: rowData?.job_number ? `#${rowData.job_number}` : rowData?.call_seq || 'this call',
      agentName: rowData?.agent_name || 'an unassigned agent',
      state: rowState(rowData),
    });
  };

  const getContextMenuItems = (params) => {
    if (!params.node) {
      return [];
    }
    const rowData = params?.node?.data;
    const selectedCount = params.api?.getSelectedNodes()?.length ?? 0;

    return [
      {
        name: 'Mark Call as Ended',
        disabled: !rowData?.id || selectedCount > 1,
        action: () => requestEnd(rowData),
      },
      'separator',
      {
        name: 'Export Selected (.xlsx)',
        action: () => params.api.exportDataAsExcel({onlySelected: true}),
      },
      'copy',
    ];
  };

  const columnDefs = useMemo(
    () => [
      new ColumnDefinition({field: FIELDS.ID, hide: true}),
      new ColumnDefinition({
        field: FIELDS.JOB_NUMBER,
        pinned: 'left',
        lockPinned: true,
        checkboxSelection: true,
        width: 160,
      }),
      new ColumnDefinition({
        field: FIELDS.STATE,
        // By name, not by reference: AG Grid 26 treats a function in `cellRenderer` as a plain JS
        // renderer, and a React element coming back from one throws while the grid draws the row.
        cellRenderer: 'stateRenderer',
        width: 190,
        filter: 'agSetColumnFilter',
        filterValues: Object.values(STATE_LABEL),
      }),
      new ColumnDefinition({field: FIELDS.AGENT_NAME, minWidth: 180}),
      new ColumnDefinition({field: FIELDS.PHONE, minWidth: 150}),
      new ColumnDefinition({field: FIELDS.STATUS, minWidth: 150}),
      new ColumnDefinition({field: FIELDS.AGE, filter: 'agNumberColumnFilter', width: 160}),
      new ColumnDefinition({field: FIELDS.STARTED_AT, minWidth: 180}),
      new ColumnDefinition({field: FIELDS.CALL_SEQ, minWidth: 220}),
    ],
    [],
  );

  const staleCount = gridRows.filter((r) => r.state_key !== 'live').length;

  return (
    <>
      <NavBar />
      <ThemeProvider theme={myTheme}>
        <Box sx={{height: '100%', px: 2, pt: 1}}>
          <Box sx={{alignItems: 'center', display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1}}>
            <Typography sx={{fontSize: '1.1rem', fontWeight: 700}}>
              {`Active AI Calls (${gridRows.length})`}
            </Typography>
            {totalOpen > gridRows.length && (
              <Typography sx={{color: COLORS.globalRed, fontSize: '0.9rem'}}>
                {`Showing the ${gridRows.length} most recent of ${totalOpen} open calls.`}
              </Typography>
            )}
            {staleCount > 0 && (
              <Typography sx={{color: COLORS.globalRed, fontSize: '0.9rem'}}>
                {`${staleCount} open longer than ${staleAfterMinutes} minutes — right-click a row to mark it ended.`}
              </Typography>
            )}
          </Box>

          {loading && <Typography>Loading active calls...</Typography>}
          {!loading && error && <Typography color="error">{error}</Typography>}
          {!loading && !error && gridRows.length === 0 && (
            <Typography>No AI calls are currently open.</Typography>
          )}

          {!loading && !error && gridRows.length > 0 && (
            <Box sx={{height: 'calc(100% - 60px)', width: '100%'}}>
              <MyGrid
                ls_name={'ai_active_calls_grid'}
                columnDefs={columnDefs}
                components={components}
                contextMenu={getContextMenuItems}
                rowData={gridRows}
                registerGrid={(params) => {
                  gridApiRef.current = params;
                }}
              />
            </Box>
          )}

          {pendingEnd && (
            <ConfirmModal open={true} handleClose={closeEndConfirm}>
              <Box sx={{'& .MuiTypography-root': {fontSize: '1rem'}}}>
                {pendingEnd.state === 'live' && (
                  <Typography variant={'body1'} color={COLORS.globalRed} mb={2}>
                    This call is still inside the active window — it may be in progress right now.
                    Ending it only closes the record; it does not hang up on the homeowner.
                  </Typography>
                )}
                <Typography variant={'body1'} mb={3}>
                  {'Mark call '}
                  <Typography component={'span'} fontWeight={600} color={COLORS.globalBlue}>
                    {pendingEnd.label}
                  </Typography>
                  {` (${pendingEnd.agentName}) as ended?`}
                </Typography>

                <FormSubmitButtons
                  cancelText={'No'}
                  submitText={'Yes'}
                  cancelAction={closeEndConfirm}
                  submitAction={submitEnd}
                  disableSubmit={savingEnd}
                  disableCancel={savingEnd}
                />
              </Box>
            </ConfirmModal>
          )}
        </Box>
      </ThemeProvider>
    </>
  );
};

export default AiActiveCalls;
